import React, { FC } from 'react';
import { useSelector } from 'react-redux';
import { Col, Row } from 'antd';

import { Typography } from 'app/components/common/typography';
import { i18n } from 'app/i18n';
import { selectBillingAccounts, selectBillingAccountIdOrThrow } from 'app/page/orders/redux/order-selectors';
import { BillingAccount } from 'app/page/orders/model/billing-account';
import { BillingAccountUtils } from 'app/page/orders/utils/billing-account-utils';
import { BillingRecipientType, InvoiceDeliveryMethod } from 'app/model/billing-details';

import styles from './billing-details-information.module.scss';

export const BillingDetailsInformation: FC = () => {
    const billingAccounts = useSelector(selectBillingAccounts);
    const selectedBillingAccountId = useSelector(selectBillingAccountIdOrThrow);

    const billingAccount: BillingAccount = BillingAccountUtils.getBillingAccount(
        billingAccounts,
        selectedBillingAccountId,
    );

    const { customerInformation } = billingAccount;

    if (!customerInformation) {
        throw new Error('Customer information must be present.');
    }

    const {
        billingDetails: {
            billingRecipientType,
            firstName,
            lastName,
            billingRecipientEmail,
            businessName,
            businessCvr,
            invoiceDeliveryMethod,
            businessGln,
        },
    } = customerInformation;

    const renderBillingText = (label: string, value: React.ReactNode): React.ReactNode => (
        <Row className={styles.customerDetailsRow}>
            <Col xs={8} md={6}>
                <Typography component="p" variant="body2">
                    {label}
                </Typography>
            </Col>
            <Col xs={16} md={18}>
                <Typography component="p" variant="body2" className={styles.text}>
                    {value}
                </Typography>
            </Col>
        </Row>
    );

    const renderBillingDetailsForBusiness = (): React.ReactNode => {
        switch (invoiceDeliveryMethod) {
            case InvoiceDeliveryMethod.EMAIL:
                return renderBillingText(i18n.summary.billingDetails.email.label, billingRecipientEmail);
            case InvoiceDeliveryMethod.EAN:
                return renderBillingText(i18n.summary.billingDetails.gln.label, businessGln);
            default:
                return null;
        }
    };

    const renderBillingDetails = (): React.ReactNode => {
        switch (billingRecipientType) {
            case BillingRecipientType.PRIVATE:
                return (
                    <>
                        {renderBillingText(i18n.summary.billingDetails.customerName, `${firstName} ${lastName}`)}
                        {renderBillingText(i18n.summary.billingDetails.email.label, billingRecipientEmail)}
                    </>
                );
            case BillingRecipientType.BUSINESS:
                return (
                    <>
                        {renderBillingText(i18n.summary.billingDetails.nameOfBusiness, businessName)}
                        {renderBillingText(i18n.summary.billingDetails.cvr.label, businessCvr)}
                        {renderBillingDetailsForBusiness()}
                    </>
                );
            default:
                throw new Error(`Not supported billing recipient type ${billingRecipientType}`);
        }
    };

    return <>{renderBillingDetails()}</>;
};
